import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAdminAppeals,
  resolveAdminAppeal,
  badgeStatus,
  formatIsoDate,
} from '../../lib/reportsAdminApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnDanger,
  BtnRow,
  Card,
  Table,
  Badge,
  ErrorText,
  Field,
} from '../../styles/adminStyles';

const STATUS_VALUES = ['pending', 'all', 'accepted', 'rejected'];

export default function AppealListPage() {
  const { t, tEnum, language } = usePortalT();
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [status, setStatus] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAdminAppeals({ status });
      setAppeals(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const onResolve = async (appealId, decision) => {
    if (busy) return;
    let restoreCredit = 0;
    let note = '';
    if (decision === 'accept') {
      const raw = window.prompt(t('reports.promptRestoreAccept'), '0');
      if (raw === null) return;
      restoreCredit = Math.max(0, Math.min(100, Number(raw) || 0));
      note = window.prompt(t('reports.promptNote'), '') || '';
    } else {
      note = window.prompt(t('reports.promptRejectReason'), '') || '';
    }
    setBusy(true);
    setMsg(null);
    setError(null);
    const res = await resolveAdminAppeal(appealId, { decision, note: note || null, restoreCredit });
    setBusy(false);
    if (res.success) {
      setMsg(decision === 'accept' ? t('reports.appealAccepted') : t('reports.appealRejected'));
      load();
    } else {
      setError(res.error || t('reports.appealFailed'));
    }
  };

  return (
    <div>
      <PageTitle>{t('appeals.title')}</PageTitle>
      <PageSubtitle>{t('appeals.subtitle')}</PageSubtitle>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field style={{ marginBottom: 0, minWidth: 150 }}>
            {t('common.status')}
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_VALUES.map((v) => (
                <option key={v} value={v}>{tEnum('appealStatusFilter', v)}</option>
              ))}
            </select>
          </Field>
          <Btn type="button" onClick={load}>{t('common.refresh')}</Btn>
          <Link to="/admin/reports"><Btn>{t('appeals.backToReports')}</Btn></Link>
        </div>
      </Card>

      {msg ? <p style={{ color: '#15803d', fontWeight: 700 }}>{msg}</p> : null}
      {error ? <ErrorText>{error}</ErrorText> : null}
      {loading ? <p>{t('common.loading')}</p> : null}

      {!loading && appeals.length === 0 ? <Card>{t('appeals.emptyFiltered')}</Card> : null}

      {!loading && appeals.length > 0 ? (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table>
            <thead>
              <tr>
                <th>{t('appeals.colAppellant')}</th>
                <th>{t('appeals.colOrigCategory')}</th>
                <th>{t('appeals.colReason')}</th>
                <th>{t('common.status')}</th>
                <th>{t('appeals.colTime')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {appeals.map((ap) => (
                <tr key={ap.id}>
                  <td style={{ fontWeight: 700 }}>{ap.userName}</td>
                  <td>
                    <div>{tEnum('reportCategory', ap.reportCategory)}</div>
                    <Badge $status={badgeStatus(ap.reportStatus)} style={{ marginTop: 4 }}>
                      {tEnum('reportStatus', ap.reportStatus)}
                    </Badge>
                  </td>
                  <td style={{ maxWidth: 320, fontSize: 13 }}>{ap.reason || t('appeals.noReason')}</td>
                  <td><Badge $status={ap.status === 'pending' ? 'active' : 'ended'}>{tEnum('appealStatus', ap.status)}</Badge></td>
                  <td style={{ fontSize: 12 }}>{formatIsoDate(ap.createdAt, language)}</td>
                  <td>
                    {ap.status === 'pending' ? (
                      <BtnRow style={{ marginTop: 0 }}>
                        <Btn $primary type="button" disabled={busy} onClick={() => onResolve(ap.id, 'accept')}>{t('appeals.accept')}</Btn>
                        <BtnDanger type="button" disabled={busy} onClick={() => onResolve(ap.id, 'reject')}>{t('appeals.reject')}</BtnDanger>
                        <Link to={`/admin/reports/${ap.reportId}`}>{t('common.detail')}</Link>
                      </BtnRow>
                    ) : (
                      <Link to={`/admin/reports/${ap.reportId}`}>{t('common.detail')}</Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}
